import React from 'react';
import propTypes from 'prop-types';
import { Form } from 'shared-ui';
import { formatDate, isDefined, isValidDate, ObjectAccessor, strIncludes } from '@dateam/ark';
import { concatClassName } from '@dateam/ark-react';
import { validate as validator, ValidationError } from 'data/observation';
import { STAGE_VALUES } from 'data/observation/observation1';
import { ObservationProps, plotObservationPropTypes } from './types';
import { ObservationErrors } from './components';
import ObservationField from './ObservationField';
import ObservationItem from './ObservationItem';
import styles from './Observation.module.scss';

const observationKey = 'observation1';

type Props = ObservationProps<App.Observation1>;

const Observation1: React.FC<Props> = ({
    data,
    plot,
    ignorable,
    className,
    onChange
}: Props) => {
    const [validationErrors, setValidationErrors] = React.useState<Record<string, ValidationError>>({});
    const internalData = React.useRef(data);

    React.useEffect(() => {
        internalData.current = data;
    }, [data]);

    React.useEffect(() => {
        if (data.stage === undefined) return;

        setValidationErrors(validator(data, plot));
    }, [plot, data]);

    const stage = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.stage ?? undefined;
    }, [data]);

    const previousValue = React.useMemo(() => {
        if (isDefined(internalData.current.previous)) {
            return (
                <>
                    {isValidDate(internalData.current.previous.updatedOn) && (
                        <div className={styles['previousDate']}>{formatDate(internalData.current.previous.updatedOn)}</div>
                    )}
                    <div className={styles['previous']}>Stade: {internalData.current.previous.stage?.value ?? '-'}</div>
                </>
            );
        }

        return (<div>Aucune valeur disponible</div>);
    }, [internalData.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIgnoreObservation = React.useCallback(() => {
        const newData: App.Observation1 = {
            ...internalData.current,
            stage: null,
            ignored: !internalData.current.ignored
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleStageOptionChange = React.useCallback((newValue: string) => {
        const newData: App.Observation1 = {
            ...internalData.current,
            stage: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleGetOptions = React.useCallback(async (value: string) => {
        if (value.length === 0) return STAGE_VALUES;

        return STAGE_VALUES.filter(item => strIncludes(value, item, { caseInsensitive: true }));
    }, []);

    const handleFocus = React.useCallback((field: ObjectAccessor<Omit<App.Observation1, 'previous'>>) => {
        if (internalData.current[field] !== undefined) return;

        const newData: App.Observation1 = {
            ...internalData.current,
            [field]: null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    return (
        <ObservationItem
            data={data}
            ignorable={ignorable}
            onIgnore={handleIgnoreObservation}
            previous={previousValue}
            label="Stade phénologique"
        >
            <div className="row">
                <ObservationField
                    id={`${observationKey}-stage`}
                    label="Stade"
                    className={concatClassName(styles[`${observationKey}-stage`], 'col-4')}
                >
                    <Form.Autocomplete<string>
                        value={stage}
                        onGetOptions={handleGetOptions}
                        onChange={handleStageOptionChange}
                        state={validationErrors['stage']?.type}
                        onFocus={() => handleFocus('stage')}
                        required={!data.ignored}
                        readOnly={data.ignored}
                    />
                </ObservationField>
            </div>
            <ObservationErrors value={validationErrors} observationKey={observationKey} />
        </ObservationItem>
    );
};

Observation1.propTypes = {
    data: propTypes.any.isRequired,
    plot: plotObservationPropTypes.isRequired,
    className: propTypes.string,
    onChange: propTypes.func
};

Observation1.defaultProps = {
    className: undefined,
    onChange: undefined
};

export default Observation1;
